import type { ReactNode } from 'react'

interface ConfidenceGateProps {
  icon: ReactNode
  title: string
  /** Required, no default — a generic placeholder here would say "we
   * don't actually know why this is untrustworthy", which is worse than
   * showing nothing. Every caller must say exactly what was missing. */
  description: string
  action?: {
    label: string
    onClick: () => void
  }
  /** Same convention as EmptyState's `compact` — smaller icon/padding for
   * an inline spot, never a different content contract. Default (false)
   * is the full-panel layout. */
  compact?: boolean
}

/**
 * ConfidenceGate — "this ran, but there wasn't enough real signal to trust
 * the result" (Story #6172 Task #6211, Streams 4/5's shared consumer).
 *
 * Deliberately NOT EmptyState: EmptyState means "nothing has happened
 * yet" (no scan run, no data uploaded). This means the opposite — a
 * feature DID run and DID produce a result, but the result is a
 * degenerate fallback (F11: no real project content, so Bedrock was
 * never called; F8: a neutral momentum default / zero academic backing /
 * a single-owner region landed on the real scoring formula). Same
 * icon/title/description/action/compact shape as EmptyState so the two
 * stay visually consistent, but amber rather than iflow-blue so a reader
 * can tell the two states apart at a glance, matching this app's existing
 * caution/unavailable colour convention.
 */
export default function ConfidenceGate({ icon, title, description, action, compact = false }: ConfidenceGateProps) {
  if (compact) {
    return (
      <div className="flex flex-col items-center justify-center py-4 px-3 text-center" data-testid="confidence-gate-compact">
        <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-2 [&>svg]:w-4 [&>svg]:h-4">
          {icon}
        </div>
        <h3 className="text-xs font-semibold text-gray-700 mb-0.5">{title}</h3>
        <p className="text-[11px] text-gray-500 max-w-xs mb-2">{description}</p>
        {action && (
          <button
            onClick={action.onClick}
            className="px-2.5 py-1 bg-amber-600 text-white text-[11px] font-medium rounded-md hover:bg-amber-700 transition-colors"
          >
            {action.label}
          </button>
        )}
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-testid="confidence-gate">
      <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-1">{title}</h3>
      <p className="text-sm text-gray-600 max-w-sm mb-6">{description}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
